import React, { useState, useMemo, useEffect, useRef } from "react";
import { Pencil, Eye, Search, Download, ChevronLeft, ChevronRight, Grid, Filter, FileText } from "lucide-react";
import EditStaffModal from "../pages/hrDashboard/edit-staff-modal";
import ViewStaffModal from "../components/ViewStaffModal";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const logo = "/logo.jpeg";

export default function StaffTable({ staff = [], onStaffUpdated, loading = false }) {
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStaffId, setSelectedStaffId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewStaffId, setViewStaffId] = useState(null);

  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const staffPerPage = 15;

  const roleLabels = {
    admin: "Admin",
    hr_officer: "HR Officer",
    directors: "Directors",
    head_of_operations: "Head of Operations",
    finance_officer: "Finance Officer",
    operations_associate: "Operations Associate",
    program_manager: "Project Manager",
    program_director: "Program Director",
    field_officer: "Field Officer",
  };

  const flattenedStaff = useMemo(() => {
    return staff.map(member => ({
      ...member,
      villageOrBlock: member.residence?.villageOrBlock || "",
      payam: member.residence?.payam || "",
      county: member.residence?.county || "",
      state: member.residence?.state || "",
      bankName: member.bankingDetails?.[0]?.bankName || "",
      bankBranch: member.bankingDetails?.[0]?.bankBranch || "",
      accountName: member.bankingDetails?.[0]?.accountName || "",
      accountNumber: member.bankingDetails?.[0]?.accountNumber || "",
      currency: member.bankingDetails?.[0]?.currency || "",
      swiftCode: member.bankingDetails?.[0]?.swiftCode || "",
      institution: member.qualifications?.[0]?.institution || "",
      award: member.qualifications?.[0]?.award || "",
      yearFrom: member.qualifications?.[0]?.yearFrom || "",
      yearTo: member.qualifications?.[0]?.yearTo || "",
      keyExpertise: member.workHistory?.keyExpertise || "",
      recentEmployer: member.workHistory?.recentEmployer || "",
      workrole: member.workHistory?.workrole || "",
      periodOfService: member.workHistory?.periodOfService || "",
      recentGrossAnnualPaymentRange: member.workHistory?.recentGrossAnnualPaymentRange || "",
      nokName: member.nextOfKin?.name || "",
      nokSex: member.nextOfKin?.sex || "",
      nokNationality: member.nextOfKin?.nationality || "",
      nokRelationship: member.nextOfKin?.relationship || "",
      nokVillageOrBlock: member.nextOfKin?.residence?.villageOrBlock || "",
      nokPayam: member.nextOfKin?.residence?.payam || "",
      nokCounty: member.nextOfKin?.residence?.county || "",
      nokState: member.nextOfKin?.residence?.state || "",
      nokTel: member.nextOfKin?.tel || "",
      nokMobile: member.nextOfKin?.mobile || "",
      nokEmail: member.nextOfKin?.email || "",
    }));
  }, [staff]);

  const filteredStaff = useMemo(() => {
    const q = search.toLowerCase();
    return flattenedStaff.filter((m) =>
      [m.firstName, m.lastName, m.email, m.jobTitle]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(q))
    );
  }, [flattenedStaff, search]);

  const totalPages = Math.ceil(filteredStaff.length / staffPerPage);

  const currentStaff = useMemo(() => {
    const start = (currentPage - 1) * staffPerPage;
    return filteredStaff.slice(start, start + staffPerPage);
  }, [filteredStaff, currentPage]);

  useEffect(() => setCurrentPage(1), [search]);

  const openEditModal = (id) => {
    setSelectedStaffId(id);
    setShowEditModal(true);
  };

  const openViewModal = (id) => {
    setViewStaffId(id);
    setShowViewModal(true);
  };

  const mapStaffToExportRow = (member) => ({
    "First Name": member.firstName || "",
    "Last Name": member.lastName || "",
    Email: member.email || "",
    Gender: member.gender || "",
    "Date of Birth": member.dateOfBirth || "",
    Role: roleLabels[member.role] || member.role || "",
    "Marital Status": member.maritalStatus || "",
    Nationality: member.nationality || "",
    "National ID or Passport": member.nationalIdOrPassport || "",
    "Village or Block": member.villageOrBlock || "",
    Payam: member.payam || "",
    County: member.county || "",
    State: member.state || "",
    Telephone: member.tel || "",
    Mobile: member.mobile || "",
    "Job Title": member.jobTitle || "",
    Department: member.department || "",
    "Date of Employment": member.dateOfEmployment || "",
    "Employment Type": member.employmentType || "",
    "Contract Duration": member.contractDuration || "",
    "Salary Range": member.salaryRange || "",
    "Bank Name": member.bankName || "",
    "Bank Branch": member.bankBranch || "",
    "Account Name": member.accountName || "",
    "Account Number": member.accountNumber || "",
    Currency: member.currency || "",
    "SWIFT Code": member.swiftCode || "",
    Institution: member.institution || "",
    Award: member.award || "",
    "Year From": member.yearFrom || "",
    "Year To": member.yearTo || "",
    "Key Expertise": member.keyExpertise || "",
    "Recent Employer": member.recentEmployer || "",
    "Work Role": member.workrole || "",
    "Period of Service": member.periodOfService || "",
    "Recent Gross Annual Payment Range": member.recentGrossAnnualPaymentRange || "",
    "Next of Kin Name": member.nokName || "",
    "Next of Kin Sex": member.nokSex || "",
    "Next of Kin Nationality": member.nokNationality || "",
    "Next of Kin Relationship": member.nokRelationship || "",
    "Next of Kin Village or Block": member.nokVillageOrBlock || "",
    "Next of Kin Payam": member.nokPayam || "",
    "Next of Kin County": member.nokCounty || "",
    "Next of Kin State": member.nokState || "",
    "Next of Kin Telephone": member.nokTel || "",
    "Next of Kin Mobile": member.nokMobile || "",
    "Next of Kin Email": member.nokEmail || "",
  });

  const exportToExcel = () => {
    if (!filteredStaff.length) {
      alert("No staff data to export!");
      return;
    }

    const exportData = filteredStaff.map(mapStaffToExportRow);

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(exportData);

    // **Add a clean, branded header to the worksheet**
    const wsHeader = [
      ["", "", "STAFF DATA REPORT"],
      ["", "", `Date: ${new Date().toLocaleDateString()}`],
    ];

    // Add the header to a new worksheet first
    const headerSheet = XLSX.utils.aoa_to_sheet(wsHeader);

    // Merge cells for the title and date for better branding
    const mergeCells = [{ s: { r: 0, c: 2 }, e: { r: 0, c: 5 } }, { s: { r: 1, c: 2 }, e: { r: 1, c: 5 } }];
    headerSheet["!merges"] = mergeCells;

    // Combine the header and data. Note: `origin` is crucial here.
    XLSX.utils.sheet_add_aoa(headerSheet, [Object.keys(exportData[0])], { origin: "A4" });
    XLSX.utils.sheet_add_json(headerSheet, exportData, { origin: "A5", skipHeader: true });

    // Set column widths based on the data
    const maxColWidths = Object.keys(exportData[0]).map((col) => {
      const maxLength = Math.max(
        col.length,
        ...exportData.map((row) => (row[col] ? row[col].toString().length : 0))
      );
      return { wch: maxLength + 2 };
    });
    headerSheet["!cols"] = maxColWidths;

    XLSX.utils.book_append_sheet(workbook, headerSheet, "Staff Report");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const dataBlob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(dataBlob, "staff_report.xlsx");
  };

  const exportToCSV = () => {
    if (!filteredStaff.length) {
      alert("No staff data to export!");
      return;
    }

    const exportData = filteredStaff.map(mapStaffToExportRow);

    const header = `"Organization Staff Report"\n"Date: ${new Date().toLocaleDateString()}"\n\n`;
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const csv = header + XLSX.utils.sheet_to_csv(worksheet);

    const csvBlob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(csvBlob, "staff_data_export.csv");
  };

  const exportToPDF = () => {
    if (!filteredStaff.length) {
      alert("No staff data to export!");
      return;
    }

    const exportData = filteredStaff.map(mapStaffToExportRow);
    const doc = new jsPDF("l", "pt", "a4");

    const img = new Image();
    img.src = logo;

    img.onload = () => {
      const margin = 40;
      const pageWidth = doc.internal.pageSize.width;
      const logoWidth = 60;
      const logoHeight = img.height * (logoWidth / img.width);

      // Add a clean header with logo and branding
      doc.addImage(img, 'JPEG', margin, margin, logoWidth, logoHeight);

      doc.setFontSize(24);
      doc.setTextColor(51, 51, 51);
      doc.text("Organization Staff Report", pageWidth / 2, margin + 20, { align: 'center' });

      doc.setFontSize(12);
      doc.setTextColor(102, 102, 102);
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, pageWidth / 2, margin + 40, { align: 'center' });

      doc.setDrawColor(220, 220, 220);
      doc.line(margin, margin + 60, pageWidth - margin, margin + 60);

      const columns = Object.keys(exportData[0]);

      autoTable(doc, {
        startY: margin + 70,
        head: [columns],
        body: exportData.map((row) => columns.map((col) => row[col])),
        theme: "grid",
        styles: {
          fontSize: 8,
          cellPadding: 3,
          textColor: [51, 51, 51],
          lineColor: [220, 220, 220] // Light gray lines
        },
        headStyles: {
          fillColor: [240, 240, 240], // Very light gray header background
          textColor: [51, 51, 51],
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [255, 255, 255]
        },
        margin: { top: 70, left: margin, right: margin },
        columnStyles: {
          0: { cellWidth: 80 },
          1: { cellWidth: 80 },
          2: { cellWidth: 100 },
          3: { cellWidth: 100 }
        },
        didDrawPage: (data) => {
          const pageCount = doc.internal.getNumberOfPages();
          doc.setFontSize(10);
          doc.setTextColor(150);
          doc.text(
            `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`,
            pageWidth - margin,
            doc.internal.pageSize.height - 20,
            { align: 'right' }
          );
        },
      });

      doc.save("staff_report.pdf");
    };
    img.onerror = () => {
      console.error("Failed to load logo image.");
      const doc = new jsPDF("l", "pt", "a4");
      doc.text("Staff Data Report", 40, 40);
      autoTable(doc, { startY: 60, head: [Object.keys(exportData[0])], body: exportData.map(row => Object.values(row)) });
      doc.save("staff_data_report.pdf");
    };
  };

  const handleExport = (format) => {
    setExportDropdownOpen(false);
    switch (format) {
      case "excel":
        exportToExcel();
        break;
      case "csv":
        exportToCSV();
        break;
      case "pdf":
        exportToPDF();
        break;
      default:
        alert("Unsupported export format");
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setExportDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="mt-8 bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Search & Export Controls */}
      <div className="p-4 bg-gray-50 border-b border-gray-200">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="relative flex-1 max-w-xl">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="text-gray-400" />
            </div>
            <input
              type="search"
              placeholder="Search staff by name, email, or role"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
            />
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Filter className="text-orange-500" />
              <span>{filteredStaff.length} records found</span>
            </div>
            
            <div className="relative inline-block text-left" ref={dropdownRef}>
              <button
                onClick={() => setExportDropdownOpen((open) => !open)}
                type="button"
                className="inline-flex items-center justify-center w-full rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-2.5 text-white shadow-sm hover:from-orange-600 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all"
              >
                <Download className="mr-2 -ml-1 h-5 w-5" />
                Export
                <svg className="ml-2 -mr-1 h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd"/>
                </svg>
              </button>
              {exportDropdownOpen && (
                <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-50 focus:outline-none">
                  <div className="py-1">
                    {["excel", "csv", "pdf"].map((format) => (
                      <button
                        key={format}
                        onClick={() => handleExport(format)}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-700"
                      >
                        <FileText className="mr-2 h-4 w-4" />
                        Export as {format.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
        </div>
      )}

      {/* Desktop Table */}
      {!loading && (
        <>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-orange-500 uppercase tracking-wider">Name</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-orange-500 uppercase tracking-wider">Email</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-orange-500 uppercase tracking-wider">User Role</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-orange-500 uppercase tracking-wider">Job Title</th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-orange-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentStaff.length > 0 ? (
                  currentStaff.map((member) => (
                    <tr key={member._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{member.firstName} {member.lastName}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{member.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{roleLabels[member.role] || member.role || "-"}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{member.jobTitle || "-"}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => openViewModal(member._id)}
                            className="text-orange-600 hover:text-orange-900 flex items-center"
                          >
                            <Eye className="mr-1 h-4 w-4" /> View
                          </button>
                          <button
                            onClick={() => openEditModal(member._id)}
                            className="text-orange-600 hover:text-orange-900 flex items-center"
                          >
                            <Pencil className="mr-1 h-4 w-4" /> Edit
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <Grid className="h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No staff found</h3>
                        <p className="mt-1 text-sm text-gray-500">Try adjusting your search or filter criteria</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden p-4 space-y-4">
            {currentStaff.length > 0 ? (
              currentStaff.map((member) => (
                <div key={member._id} className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                  <div className="px-4 py-5 sm:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">{member.firstName} {member.lastName}</h3>
                        <p className="text-sm text-gray-500">{member.email}</p>
                      </div>
                    </div>
                    <dl className="mt-4 grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">User Role</dt>
                        <dd className="mt-1 text-sm text-gray-900">{roleLabels[member.role] || member.role || "-"}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Job Title</dt>
                        <dd className="mt-1 text-sm text-gray-900">{member.jobTitle || "-"}</dd>
                      </div>
                    </dl>
                    <div className="mt-6 flex space-x-3">
                      <button
                        onClick={() => openViewModal(member._id)}
                        className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <Eye className="mr-2 -ml-1 h-5 w-5" />
                        View
                      </button>
                      <button
                        onClick={() => openEditModal(member._id)}
                        className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700"
                      >
                        <Pencil className="mr-2 -ml-1 h-5 w-5" />
                        Edit
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <Grid className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No staff found</h3>
                <p className="mt-1 text-sm text-gray-500">Try adjusting your search or filter criteria</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="mr-2 h-5 w-5" />
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="ml-2 h-5 w-5" />
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(currentPage - 1) * staffPerPage + 1}</span> to{" "}
                    <span className="font-medium">
                      {Math.min(currentPage * staffPerPage, filteredStaff.length)}
                    </span>{" "}
                    of <span className="font-medium">{filteredStaff.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const page = i + 1;
                      // Show first page, current page, last page, and pages around current page
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === page
                                ? "z-10 bg-orange-50 border-orange-500 text-orange-600"
                                : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (
                        (page === 2 && currentPage > 4) ||
                        (page === totalPages - 1 && currentPage < totalPages - 3)
                      ) {
                        return (
                          <span
                            key={page}
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                          >
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {showEditModal && (
        <EditStaffModal
          staffId={selectedStaffId}
          closeModal={() => setShowEditModal(false)}
          onUpdateSuccess={() => {
            setShowEditModal(false);
            onStaffUpdated?.();
          }}
        />
      )}

      {showViewModal && (
        <ViewStaffModal
          staffId={viewStaffId}
          closeModal={() => setShowViewModal(false)}
        />
      )}
    </div>
  );
}